"use client"

import { useState, useEffect, useMemo } from "react"
import { useLanguage } from "@/contexts/language-context"
import { LanguageSelector } from "@/components/mobile/language-selector"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeft, Wallet, Shield, Clock, CheckCircle, Copy, ExternalLink, Zap, TrendingUp, User, AlertTriangle, Loader2, Smartphone, Info } from "lucide-react"
import OrderStatus from "@/components/mobile/order-status"
import { EmailVerificationScreen } from "@/components/mobile/email-verification-screen"
import { CasinoPinConfirmation } from "@/components/mobile/pin-confirmation"
import TwoFactorModal from "@/components/TwoFactorModal"
import { useNBCBalance } from "@/hooks/use-nbc-balance"
import { useCommissionFees } from "@/hooks/use-commission-fees"
import { getExchangeRate, getCurrencySymbol } from "@/lib/services/exchange-rate"
import { confirmEmailVerified } from "@/lib/services/partners-api"
import type { NBCUser } from "@/types/nbc-types"

type Step = "email-verify" | "amount" | "pin" | "payment" | "status"

interface MobileDepositFlowProps {
  onComplete?: (amount: number) => void
  userEmail?: string
  onBack?: () => void
  prefilledOrderData?: any // Datos completos de la orden del token seguro
  prefilledUser?: NBCUser | null // Usuario NBC prellenado desde verificación
}

export function MobileDepositFlow({ onComplete, userEmail, onBack, prefilledOrderData, prefilledUser }: MobileDepositFlowProps) {
  const { t } = useLanguage()
  
  const [currentStep, setCurrentStep] = useState<Step>(() => {
    console.log('🔍 [MobileDepositFlow] Inicializando - userEmail:', userEmail, 'prefilledOrderData:', prefilledOrderData, 'prefilledUser:', prefilledUser);
    
    // Si tenemos datos prellenados, usar esa lógica para determinar el paso inicial
    if (prefilledOrderData) {
      console.log('🔍 [MobileDepositFlow] Tiene datos prellenados - amount:', prefilledOrderData.amount);
      if (prefilledOrderData.amount && Number(prefilledOrderData.amount) > 0) {
        // Si tenemos cantidad > 0 y email verificado válido, ir directo al pago
        if (userEmail && userEmail.includes("@")) {
          console.log('✅ [MobileDepositFlow] Iniciando en PAYMENT - cantidad prellenada y email válido');
          return "payment"
        }
      }
      // Si no hay cantidad o es 0, ir a selección de cantidad si el email es válido
      if (userEmail && userEmail.includes("@")) {
        console.log('✅ [MobileDepositFlow] Iniciando en AMOUNT - email válido pero sin cantidad');
        return "amount"
      }
    }
    
    // Lógica por defecto: verificar si el email es NBC TALK PAY
    if (!userEmail || !userEmail.includes("nbctalk")) {
      console.log('✅ [MobileDepositFlow] Iniciando en EMAIL-VERIFY - sin email válido');
      return "email-verify"
    }
    
    console.log('✅ [MobileDepositFlow] Iniciando en AMOUNT - email NBC válido');
    return "amount"
  })
  const [verifiedEmail, setVerifiedEmail] = useState<string>(() => {
    // Si tenemos userEmail válido (viene del email verificado), usarlo inmediatamente
    if (userEmail && userEmail.includes("@")) {
      return userEmail
    }
    return ""
  })
  
  const [nbcUser, setNbcUser] = useState<NBCUser | null>(() => {
    // Si tenemos prefilledUser (viene de la verificación en deposit/page.tsx), usarlo inmediatamente
    if (prefilledUser) {
      console.log('✅ [MobileDepositFlow] Inicializando con usuario prellenado:', prefilledUser);
      return prefilledUser;
    }
    return null;
  })
  
  const [amount, setAmount] = useState("")
  const [displayCurrency, setDisplayCurrency] = useState<string>("EUR") // Moneda del partner
  const [selectedCurrency, setSelectedCurrency] = useState<"USD" | string>("USD") // USD=USDT o displayCurrency
  const [selectedQuickAmount, setSelectedQuickAmount] = useState<string | null>(null)
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null)
  
  // Reset quick selection when currency changes
  useEffect(() => {
    setSelectedQuickAmount(null);
  }, [selectedCurrency]);

  // Set default amount to show payment details
  useEffect(() => {
    if (!amount) {
      setAmount("10");
    }
  }, []);

  // Prellenar datos desde token seguro si están disponibles
  useEffect(() => {
    if (prefilledOrderData) {
      console.log("📦 [Deposit] Datos de token recibidos:", prefilledOrderData);
      
      // Cargar display_currency del partner
      if (prefilledOrderData.display_currency) {
        console.log(`💱 [Deposit] Setting display currency from partner: ${prefilledOrderData.display_currency}`);
        setDisplayCurrency(prefilledOrderData.display_currency);
      } else {
        console.warn("⚠️ [Deposit] No display_currency in prefilledOrderData, using default EUR");
      }
      
      if (prefilledOrderData.email && !verifiedEmail) {
        // Solo usar el email del token si no hay uno verificado ya
        setVerifiedEmail(prefilledOrderData.email);
      }
      
      // Determinar el paso inicial basado en si tenemos cantidad prellenada
      console.log("🔄 [useEffect] Verificando pasos - verifiedEmail:", verifiedEmail, "amount:", prefilledOrderData.amount);
      
      if (prefilledOrderData.amount && Number(prefilledOrderData.amount) > 0) {
        // Si tenemos cantidad > 0 y email verificado válido, ir directo al pago
        if (verifiedEmail && verifiedEmail.includes("@")) {
          console.log("✅ [useEffect] Cambiando a PAYMENT - email verificado y cantidad prellenada");
          setCurrentStep("payment");
        } else {
          console.log("⚠️ [useEffect] Cambiando a EMAIL-VERIFY - sin email verificado");
          setCurrentStep("email-verify");
        }
      } else {
        // Si cantidad es 0 o no existe, ir a verificación de email o selección de cantidad
        if (verifiedEmail && verifiedEmail.includes("@")) {
          console.log("✅ [useEffect] Cambiando a AMOUNT - email verificado pero sin cantidad");
          setCurrentStep("amount");
        } else {
          console.log("⚠️ [useEffect] Cambiando a EMAIL-VERIFY - sin email verificado");
          setCurrentStep("email-verify");
        }
      }
      
      if (prefilledOrderData.amount && Number(prefilledOrderData.amount) > 0) {
        setAmount(prefilledOrderData.amount.toString());
      }
      
      // Actualizar orderData con los datos del token
      if (prefilledOrderData.order_id || prefilledOrderData.payment_address) {
        setOrderData(prev => ({
          ...prev,
          orderId: prefilledOrderData.partner_order_id || prefilledOrderData.order_id || prev.orderId,
          paymentAddress: prefilledOrderData.payment_address || prev.paymentAddress,
          expiresAt: prefilledOrderData.expires_at ? new Date(prefilledOrderData.expires_at) : prev.expiresAt,
          amountUsdt: prefilledOrderData.amount ? prefilledOrderData.amount.toString() : prev.amountUsdt
        }));
      }
    }
  }, [prefilledOrderData]);

  // Cargar usuario NBC automáticamente cuando tenemos email verificado válido
  useEffect(() => {
    const loadNBCUserFromVerifiedEmail = async () => {
      if (verifiedEmail && verifiedEmail.includes("@") && !nbcUser) {
        try {
          console.log("🔍 Cargando usuario NBC para email verificado:", verifiedEmail);
          const { validateNBCUser } = await import("@/lib/services/nbc-api");
          const user = await validateNBCUser(verifiedEmail);
          if (user?.success) {
            setNbcUser(user);
            console.log("✅ Usuario NBC cargado para email verificado:", user);
          }
        } catch (error) {
          console.error("❌ Error cargando usuario NBC para email verificado:", error);
        }
      }
    };

    loadNBCUserFromVerifiedEmail();
  }, [verifiedEmail, nbcUser]);

  // Determinar si la cantidad está prellenada y no debe modificarse
  const isAmountPrefilled = prefilledOrderData && prefilledOrderData.amount && Number(prefilledOrderData.amount) > 0;
  
  const [validationError, setValidationError] = useState<string | null>(null) // Error de validación
  const [pin, setPin] = useState("")
  const [isCompleting, setIsCompleting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [exchangeRate, setExchangeRate] = useState(1.0) // Inicializar en 1.0 (USD=USDT), se actualiza con la moneda del partner
  const [timeLeft, setTimeLeft] = useState(30 * 60) // 30 minutes in seconds
  const [showPinConfirmation, setShowPinConfirmation] = useState(false)
  const [transactionId, setTransactionId] = useState<string | null>(null)
  const [transactionError, setTransactionError] = useState<string | null>(null)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [sessionToken, setSessionToken] = useState<string | null>(null) // Token de sesión del PIN
  
  // Estados para 2FA
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [twoFactorMethod, setTwoFactorMethod] = useState<'totp' | 'email'>('email')
  const [twoFactorCode, setTwoFactorCode] = useState<string | null>(null)
  const [otpId, setOtpId] = useState<string | null>(null)
  const [pendingTransaction, setPendingTransaction] = useState<any | null>(null)
  
  // Hook para obtener balance real
  const { balance: userBalance, isLoading: isLoadingBalance, error: balanceError } = useNBCBalance(nbcUser || undefined)
  
  // Hook para calcular comisiones
  // Hook para obtener comisiones - siempre en USDT
  const usdtAmount = selectedCurrency !== "USD" && amount 
    ? (Number.parseFloat(amount) / exchangeRate) 
    : (Number.parseFloat(amount) || 0);
  const { fees, currentFee, totalAmount, isLoading: isLoadingFees } = useCommissionFees(usdtAmount)
  
  const [orderData, setOrderData] = useState<{
    orderId: string;
    amountUsdt: string;
    amountEur: string;
    paymentAddress: string;
    expiresAt: Date;
    status: "pending" | "processing" | "completed" | "failed";
    email?: string;
  }>({
    orderId: "ORD-12345",
    amountUsdt: "100.00",
    amountEur: "92.50",
    paymentAddress: "0x7489e83D4d1B129ff0eF91215eBe9eCFe8bf6b6d",
    expiresAt: new Date(Date.now() + 30 * 60 * 1000), // 30 minutes from now
    status: "pending",
    email: "",
  })

  useEffect(() => {
    if (currentStep === "payment" && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1)
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [currentStep, timeLeft])

  // Contador de redirección automática después del éxito
  useEffect(() => {
    if (transactionId && prefilledOrderData?.callback_url) {
      // Iniciar contador de 10 segundos
      setRedirectCountdown(10);
      
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev === null) return null;
          if (prev <= 1) {
            // Redirigir cuando llegue a 0
            clearInterval(countdownInterval);
            window.location.href = prefilledOrderData.callback_url!;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [transactionId, prefilledOrderData?.callback_url])

  useEffect(() => {
    // Actualizar tasa de cambio real cada 30 segundos
    const updateExchangeRate = async () => {
      try {
        console.log(`📊 [Deposit] Fetching exchange rate for ${displayCurrency}...`);
        const rate = await getExchangeRate(displayCurrency);
        console.log(`✅ [Deposit] Exchange rate received: 1 USDT = ${rate} ${displayCurrency}`);
        setExchangeRate(rate);
      } catch (error) {
        console.error('❌ [Deposit] Error updating exchange rate:', error);
      }
    };

    // Obtener tasa inicial
    updateExchangeRate();

    // Actualizar cada 30 segundos
    const interval = setInterval(updateExchangeRate, 30000);
    return () => clearInterval(interval);
  }, [displayCurrency])

  // Memoizar límites de monto desde la configuración del partner
  const minAmount = useMemo(() => {
    return prefilledOrderData?.minimum_amount 
      ? Number.parseFloat(prefilledOrderData.minimum_amount) 
      : 10; // Default minimum 10 USDT
  }, [prefilledOrderData]);

  const maxAmount = useMemo(() => {
    return prefilledOrderData?.maximum_amount 
      ? Number.parseFloat(prefilledOrderData.maximum_amount) 
      : 10000; // Default maximum 10000 USDT
  }, [prefilledOrderData]);

  // Validación de monto en tiempo real
  useEffect(() => {
    const numAmount = parseFloat(amount);
    let error: string | null = null;

    if (amount && !isNaN(numAmount)) {
      const totalWithFee = numAmount + currentFee;

      if (numAmount < minAmount) {
        error = t('validation.amount_too_low') + `: ${minAmount} USDT`;
      } else if (numAmount > maxAmount) {
        error = t('validation.amount_too_high') + `: ${maxAmount} USDT`;
      } else if (userBalance && totalWithFee > userBalance.raw_balance) {
        error = t('casino.insufficient_balance_message', {
          totalRequired: totalWithFee.toFixed(2),
          amount: numAmount.toString(),
          fee: currentFee.toFixed(2),
          available: userBalance.usdt
        });
      }
    } else if (amount) {
      error = t('validation.invalid_amount');
    }

    setValidationError(error);
  }, [amount, userBalance, currentFee, t, prefilledOrderData]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const handleAmountSubmit = async () => {
    if (amount && Number.parseFloat(amount) > 0) {
      // Convert to USDT if display currency is selected
      const usdtAmount = selectedCurrency !== "USD" 
        ? (Number.parseFloat(amount) / exchangeRate) 
        : Number.parseFloat(amount);
      
      // Get minimum and maximum from partner configuration
      const minAmount = prefilledOrderData?.minimum_amount 
        ? Number.parseFloat(prefilledOrderData.minimum_amount) 
        : 10; // Default minimum 10 USDT
      
      const maxAmount = prefilledOrderData?.maximum_amount 
        ? Number.parseFloat(prefilledOrderData.maximum_amount) 
        : null; // No default maximum
      
      // Validate minimum amount in USDT
      if (usdtAmount < minAmount) {
        setValidationError(t('validation.amount_too_low') + `: ${minAmount} USDT`);
        return;
      }
      
      // Validate maximum amount in USDT if defined
      if (maxAmount && usdtAmount > maxAmount) {
        setValidationError(t('validation.amount_too_high') + `: ${maxAmount} USDT`);
        return;
      }
      
      setIsLoading(true)
      
      // Update the amount in the database using confirmEmailVerified
      // La API key se maneja de forma segura en el servidor (proxy)
      console.log("🔍 [handleAmountSubmit] Checking conditions:", {
        hasOrderId: !!prefilledOrderData?.order_id,
        orderId: prefilledOrderData?.order_id,
        hasVerifiedEmail: !!verifiedEmail,
        verifiedEmail: verifiedEmail,
        amount: usdtAmount
      })
      
      if (prefilledOrderData?.order_id && verifiedEmail) {
        try {
          console.log("💾 [handleAmountSubmit] Updating order amount in database:", {
            order_id: prefilledOrderData.order_id,
            email: verifiedEmail,
            amount: usdtAmount,
          })

          // No pasar API key - el proxy del servidor la manejará
          const result = await confirmEmailVerified(
            {
              order_id: prefilledOrderData.order_id,
              email: verifiedEmail,
              amount: usdtAmount
            }
          )

          console.log("✅ [handleAmountSubmit] Amount updated successfully:", result)
        } catch (error) {
          console.error("❌ [handleAmountSubmit] Error updating amount in database:", error)
          // Log the error but continue to payment step
          // The amount is already in local state
        }
      } else {
        console.log(
          "⚠️ [handleAmountSubmit] Skipping database update - missing order_id or email:",
          {
            order_id: prefilledOrderData?.order_id,
            verifiedEmail: verifiedEmail
          }
        )
      }
      
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setOrderData((prev) => ({
        ...prev,
        amountUsdt: usdtAmount.toFixed(2),
        amountEur: selectedCurrency !== "USD" 
          ? amount 
          : (usdtAmount * exchangeRate).toFixed(2),
        // Asegurar que usamos el email verificado, no el del token original
        email: verifiedEmail || prev.email,
      }))
      setIsLoading(false)
      setCurrentStep("payment")
    }
  }

  const handlePinSubmit = async (enteredPin: string) => {
    setPin(enteredPin)
    setIsLoading(true)
    await new Promise((resolve) => setTimeout(resolve, 800))
    setIsLoading(false)
    
    // Después de confirmar el PIN, procesar el pago
    handlePaymentComplete()
  }

  const handlePaymentSent = () => {
    console.log("🔐 [MobileDepositFlow] Showing PIN confirmation for transaction signing");
    setShowPinConfirmation(true);
  };

  const handlePinSuccess = async (enteredPin: string, receivedSessionToken?: string) => {
    console.log("✅ [MobileDepositFlow] PIN verified, processing transaction");
    console.log("🔑 [MobileDepositFlow] Session token received:", receivedSessionToken ? "YES" : "NO");
    console.log("📊 [MobileDepositFlow] Current state:", {
      nbcUser: !!nbcUser,
      wallet_address: nbcUser?.wallet_address,
      public_key: nbcUser?.public_key,
      orderData: orderData,
      verifiedEmail: verifiedEmail,
      currentFee: currentFee
    });
    
    setPin(enteredPin);
    setSessionToken(receivedSessionToken || null);
    setShowPinConfirmation(false);

    // Preparar datos de la transacción
    const transactionData = {
      sender_address: nbcUser?.wallet_address || nbcUser?.public_key,
      destination_address: orderData.paymentAddress,
      token_amount: parseFloat(orderData.amountUsdt),
      comision_amount: currentFee,
      personal_key: enteredPin,
      email: verifiedEmail,
      blockchain_network: "polygon",
      token_contract_address: process.env.NEXT_PUBLIC_USDT_CONTRACT || '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      session_token: receivedSessionToken,
    };

    console.log("📤 [MobileDepositFlow] Transaction data prepared:", {
      sender_address: transactionData.sender_address,
      destination_address: transactionData.destination_address,
      token_amount: transactionData.token_amount,
      comision_amount: transactionData.comision_amount,
      email: transactionData.email,
      has_session_token: !!transactionData.session_token
    });

    // Guardar datos pendientes para uso posterior (con 2FA si es necesario)
    setPendingTransaction(transactionData);

    console.log("🚀 [MobileDepositFlow] Calling executeTransaction...");
    // Ejecutar transacción (puede requerir 2FA)
    try {
      await executeTransaction(transactionData);
    } catch (error) {
      console.error("❌ [MobileDepositFlow] Error in executeTransaction:", error);
      setTransactionError("Error al procesar la transacción");
      setOrderData((prev) => ({ ...prev, status: "failed" }));
      setIsLoading(false);
    }
  };

  const executeTransaction = async (txData: any, code?: string, otp_id?: string | null) => {
    console.log("🔄 [executeTransaction] Starting execution...");
    console.log("📦 [executeTransaction] Input data:", { 
      has_txData: !!txData, 
      has_code: !!code, 
      has_otp_id: !!otp_id 
    });

    setCurrentStep("status"); // Mostrar pantalla de estado inmediatamente
    setOrderData((prev) => ({ ...prev, status: "processing" }));
    setIsLoading(true);

    try {
      // Agregar código 2FA si está disponible
      const payload = {
        ...txData,
        ...(code && { two_factor_code: code }),
        ...(otp_id && { otp_id })
      };

      console.log("📤 [executeTransaction] Sending transaction with payload:", {
        sender_address: payload.sender_address,
        destination_address: payload.destination_address,
        token_amount: payload.token_amount,
        comision_amount: payload.comision_amount,
        email: payload.email,
        has_session_token: !!payload.session_token,
        has_2fa_code: !!payload.two_factor_code,
        has_otp_id: !!payload.otp_id
      });

      // Enviar transacción al backend NBC TALK PAY
      console.log("🌐 [executeTransaction] Calling /api/send_transaction...");
      const transactionResponse = await fetch('/api/send_transaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log("📨 [executeTransaction] Response status:", transactionResponse.status);
      const data = await transactionResponse.json();
      console.log("📨 [executeTransaction] Response data:", data);

      // ✅ MANEJO DE 2FA REQUERIDO
      if (!data.success && data.require_2fa) {
        console.log("🔐 [executeTransaction] 2FA required for transaction");
        setTwoFactorMethod(data.method || 'email');
        setShow2FAModal(true);
        setCurrentStep("payment"); // Volver a payment para mostrar modal
        setIsLoading(false);
        return;
      }

      if (data.success) {
        console.log("✅ [executeTransaction] Transaction successful!");
        const transactionGroupId = data.transaction_data;
        setTransactionId(transactionGroupId);
        setTransactionError(null);
        setOrderData((prev) => ({ ...prev, status: "completed" }));
        
        // 🔗 Actualizar partner_order con transaction_group_id para tracking
        if (prefilledOrderData?.order_id && transactionGroupId) {
          console.log('🔗 [MobileDepositFlow] Linking order with transaction_group_id:', {
            order_id: prefilledOrderData.order_id,
            transaction_group_id: transactionGroupId
          });
          
          try {
            const { updateTransactionGroup } = await import('@/lib/services/partners-api');
            const updateResult = await updateTransactionGroup(
              prefilledOrderData.order_id,
              transactionGroupId
            );
            
            if (updateResult.success) {
              console.log('✅ [MobileDepositFlow] Order successfully linked with transaction');
            } else {
              console.error('❌ [MobileDepositFlow] Failed to link order with transaction:', updateResult.error);
              // No fallar la transacción - solo logging
            }
          } catch (linkError) {
            console.error('❌ [MobileDepositFlow] Error linking order with transaction:', linkError);
            // No fallar la transacción por este error - es solo tracking
          }
        } else {
          console.log('⚠️ [MobileDepositFlow] Skipping transaction linking - missing data:', {
            hasOrderId: !!prefilledOrderData?.order_id,
            hasTransactionGroupId: !!transactionGroupId
          });
        }
      } else {
        console.error("❌ [executeTransaction] Transaction failed:", data);
        setTransactionError(data.message || t('error.transaction_failed'));
        setOrderData((prev) => ({ ...prev, status: "failed" }));
      }
    } catch (error) {
      console.error("❌ [executeTransaction] Exception caught:", error);
      setTransactionError(t('error.transaction_failed'));
      setOrderData((prev) => ({ ...prev, status: "failed" }));
    } finally {
      console.log("🏁 [executeTransaction] Execution finished");
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (code: string, otp_id?: string | null) => {
    console.log("✅ [MobileDepositFlow] 2FA code verified, retrying transaction");
    
    // Guardar código y otpId
    setTwoFactorCode(code);
    setOtpId(otp_id || null);
    setShow2FAModal(false);

    // Reintentar transacción con código 2FA
    if (pendingTransaction) {
      await executeTransaction(pendingTransaction, code, otp_id);
    } else {
      console.error("❌ [MobileDepositFlow] No pending transaction to retry");
      setTransactionError("Error: No hay transacción pendiente");
    }
  };

  const handlePinCancel = () => {
    console.log("❌ [MobileDepositFlow] PIN confirmation cancelled");
    setShowPinConfirmation(false);
  };

  const handleMaxAttemptsReached = () => {
    console.log("🚫 [MobileDepositFlow] Max PIN attempts reached, cancelling order");
    setTransactionError(t('pin.max_attempts_reached'));
    setShowPinConfirmation(false);
    setCurrentStep("status");
  };

  const handlePaymentComplete = () => {
    setOrderData((prev) => ({ ...prev, status: "processing" }))
    setCurrentStep("status")

    // Simulate status updates
    setTimeout(() => {
      setOrderData((prev) => ({ ...prev, status: "completed" }))
      if (onComplete && !isCompleting) {
        setIsCompleting(true)
        setTimeout(() => {
          onComplete(Number.parseFloat(amount))
        }, 2000) // Wait 2 seconds to show completion
      }
    }, 5000)
  }

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
  }

  const handleBackNavigation = () => {
    if (currentStep === "email-verify" && onBack) {
      onBack()
    } else if (currentStep === "amount") {
      if (!verifiedEmail || !verifiedEmail.includes("nbctalk")) {
        setCurrentStep("email-verify")
      } else if (onBack) {
        onBack()
      }
    } else if (currentStep === "payment") {
      // Si tenemos datos prellenados con cantidad > 0, cancelar significa volver al cancel_url o callback_url
      if (prefilledOrderData && prefilledOrderData.amount && Number(prefilledOrderData.amount) > 0) {
        // Prioridad 1: cancel_url (URL específica para cancelación)
        if (prefilledOrderData.cancel_url) {
          console.log("🔄 Redirigiendo a cancel_url:", prefilledOrderData.cancel_url);
          window.location.href = prefilledOrderData.cancel_url;
        } 
        // Prioridad 2: callback_url (fallback)
        else if (prefilledOrderData.callback_url) {
          console.log("🔄 Redirigiendo a callback_url (fallback):", prefilledOrderData.callback_url);
          window.location.href = prefilledOrderData.callback_url;
        } 
        // Prioridad 3: onBack callback del componente padre
        else if (onBack) {
          onBack()
        }
      } else {
        // Si no hay cantidad prellenada, permitir volver a seleccionar cantidad
        setCurrentStep("amount")
      }
    } else {
      // Navegación normal para pin y status
      if (currentStep === "pin") setCurrentStep("payment") 
      if (currentStep === "status") setCurrentStep("pin")
    }
  }

  const handleEmailVerified = async (email: string, user?: NBCUser, tokenOrderData?: any) => {
    setVerifiedEmail(email)
    
    // Actualizar orderData con el email verificado (no el del token original)
    console.log('🔄 Actualizando orderData con email verificado:', email);
    console.log('🔄 TokenOrderData recibido:', tokenOrderData);
    
    setOrderData(prev => ({
      ...prev,
      email: email,  // Usar el email verificado por el usuario
    }));
    
    if (user) {
      setNbcUser(user) // Guardar usuario NBC completo
      console.log('✅ NBC User set in deposit flow:', user)
    } else {
      // Si no se pasó el usuario, intentar cargarlo desde el email verificado
      try {
        console.log('🔍 Cargando información del usuario NBC para:', email);
        const { validateNBCUser } = await import('@/lib/services/nbc-api');
        const nbcUserResponse = await validateNBCUser(email);
        if (nbcUserResponse && nbcUserResponse.success) {
          setNbcUser(nbcUserResponse);
          console.log('✅ Usuario NBC cargado automáticamente:', nbcUserResponse);
        }
      } catch (error) {
        console.warn('⚠️ No se pudo cargar usuario NBC automáticamente:', error);
      }
    }
    
    // Decidir siguiente paso basado en datos prellenados
    if (prefilledOrderData && prefilledOrderData.amount && Number(prefilledOrderData.amount) > 0) {
      // Si tenemos cantidad prellenada > 0, saltar directo al pago
      setCurrentStep("payment")
    } else {
      // Si no hay cantidad o es 0, ir a selección de cantidad
      setCurrentStep("amount")
    }
  }

  if (currentStep === "email-verify") {
    return <EmailVerificationScreen currentEmail={userEmail} onEmailVerified={handleEmailVerified} onBack={onBack} />
  }

  const steps = [
    { key: "amount", icon: Wallet },
    { key: "payment", icon: Clock },
    { key: "pin", icon: Shield },
    { key: "status", icon: CheckCircle },
  ]

  const getCurrentStepIndex = () => steps.findIndex(step => step.key === currentStep)

  // Quick deposits adaptados para la moneda seleccionada
  const getQuickDeposits = () => {
    // Si está en USD (USDT), usar valores en USD
    if (selectedCurrency === "USD") {
      return [
        { amount: "25", label: t('casino.quick_start'), icon: "⚡", popular: false },
        { amount: "50", label: t('casino.most_popular'), icon: "🔥", popular: true },
        { amount: "100", label: t('casino.high_roller'), icon: "💎", popular: false },
        { amount: "250", label: t('casino.vip'), icon: "", popular: false },
      ];
    }
    // Para cualquier otra moneda (EUR, GBP, etc.), ajustar valores
    return [
      { amount: "20", label: t('casino.quick_start'), icon: "⚡", popular: false },
      { amount: "50", label: t('casino.most_popular'), icon: "🔥", popular: true },
      { amount: "100", label: t('casino.high_roller'), icon: "💎", popular: false },
      { amount: "250", label: t('casino.vip'), icon: "", popular: false },
    ];
  };

  // Funciones para manejar la selección de cantidad
  const handleQuickAmountClick = (quickAmount: string) => {
    setAmount(quickAmount);
    setSelectedQuickAmount(quickAmount);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = e.target.value;
    setAmount(newAmount);
    
    // Si el valor no coincide con ningún botón rápido, limpiar selección
    const quickDeposits = getQuickDeposits();
    const matchesQuickAmount = quickDeposits.some(q => q.amount === newAmount);
    if (!matchesQuickAmount) {
      setSelectedQuickAmount(null);
    }
  };

  // Mostrar PIN confirmation con header, steps y footer
  if (showPinConfirmation) {
    return (
      <div className="max-w-md mx-auto min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setShowPinConfirmation(false); setCurrentStep("payment"); }} disabled={isLoading}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm">N</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground tracking-tight">NBC TALK PAY</h1>
                <p className="text-xs text-muted-foreground font-medium">{t('casino.casino_deposit')}</p>
              </div>
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
            )}
            <LanguageSelector />
          </div>
        </div>

        {/* Steps Flow */}
        <div className="p-4 pb-0">
          <div className="flex items-center justify-center gap-2 mb-4">
            {steps.map((step, index) => {
              // Para la pantalla de PIN, los pasos completados son amount (0) y payment (1)
              const isCompleted = index < 2; // 0 y 1 en verde
              const isActive = (step.key === "pin");
              return (
                <div key={step.key} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${
                      isActive && showPinConfirmation
                        ? "bg-primary text-primary-foreground scale-110 shadow-lg"
                        : isCompleted
                          ? "bg-green-100 text-green-800 shadow-md"
                          : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <step.icon className="h-4 w-4" />
                    )}
                  </div>
                  {index < 3 && (
                    <div
                      className={`w-8 h-0.5 transition-all duration-500 ${
                        isCompleted ? "bg-green-200" : "bg-muted"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* PIN Confirmation Body */}
        <div className="p-4 pt-0">
          <CasinoPinConfirmation
            transactionType="casino_deposit"
            transactionDetails={{
              balance: userBalance?.usdt || '0.00',
              amount: `${orderData.amountUsdt} USDT`,
              fee: `${currentFee.toFixed(2)} USDT`,
              total: `${(parseFloat(orderData.amountUsdt) + currentFee).toFixed(2)} USDT`,
              recipient: orderData.paymentAddress,
              method: t('casino.casino_deposit'),
              error: '',
            }}
            email={verifiedEmail}
            onSuccess={handlePinSuccess}
            onCancel={() => { setShowPinConfirmation(false); setCurrentStep("payment"); }}
            onMaxAttemptsReached={handleMaxAttemptsReached}
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="text-center">
            <p className="text-xs text-muted-foreground/70">
              © 2024 {t('casino.powered_by')} <span className="font-semibold text-muted-foreground">NBC BANQ</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen bg-background">
      <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleBackNavigation} disabled={isLoading}>
            <ArrowLeft className="h-4 w-4" />
            {currentStep === "payment" && isAmountPrefilled && (
              <span className="ml-1 text-xs">{t('casino.cancel')}</span>
            )}
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">N</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">NBC TALK PAY</h1>
              <p className="text-xs text-muted-foreground font-medium">{t('casino.casino_deposit')}</p>
            </div>
          </div>
          {isLoading && (
            <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
          )}
          <LanguageSelector />
        </div>

        {verifiedEmail && currentStep === "amount" && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg animate-in slide-in-from-top-2 duration-300">
            <p className="text-sm text-green-800">✓ {t('casino.verified_account')}: {verifiedEmail}</p>
          </div>
        )}
      </div>

      <div className="p-4 space-y-6">
        <div className="flex items-center justify-center gap-2">
          {steps.map((step, index) => (
            <div key={step.key} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${
                  currentStep === step.key
                    ? "bg-primary text-primary-foreground scale-110 shadow-lg"
                    : index < getCurrentStepIndex()
                      ? "bg-green-100 text-green-800 shadow-md"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {index < getCurrentStepIndex() ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <step.icon className="h-4 w-4" />
                )}
              </div>
              {index < 3 && (
                <div
                  className={`w-8 h-0.5 transition-all duration-500 ${
                    index < getCurrentStepIndex() ? "bg-green-200" : "bg-muted"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Amount Step */}
        {currentStep === "amount" && (
          <div className="animate-in slide-in-from-right-4 duration-300">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-5 w-5" />
                  {t('casino.enter_amount')}
                </CardTitle>
                <CardDescription>
                  {t('validation.amount_too_low')}: {minAmount} USDT 
                  {exchangeRate > 0 && displayCurrency !== "USD" && ` (≈ ${getCurrencySymbol(displayCurrency)}${(minAmount * exchangeRate).toFixed(2)} ${displayCurrency})`}
                  {maxAmount && ` • Maximum: ${maxAmount} USDT`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Notificación de cantidad prellenada */}
                {isAmountPrefilled && (
                  <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Info className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">
                        Amount has been set from your secure payment link and cannot be modified.
                      </span>
                    </div>
                  </div>
                )}

                {/* Información del Usuario NBC */}
                {nbcUser?.success && (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                      <div className="space-y-2 flex-1">
                        <div className="font-medium text-green-800 text-sm">
                          ✅ NBC TALK PAY Account Verified
                        </div>
                        
                        {nbcUser.user_data && (
                          <div className="space-y-1 text-xs">
                            <div className="flex items-center gap-2">
                              <User className="h-3 w-3" />
                              <span>{nbcUser.user_data.name}</span>
                              <Badge variant="secondary" className="text-xs">
                                ID: {nbcUser.user_data.nbc_talk_id}
                              </Badge>
                            </div>
                          </div>
                        )}
                        
                        {(nbcUser.wallet_address || nbcUser.public_key) && (
                          <div className="flex items-center gap-2 text-xs">
                            <Wallet className="h-3 w-3" />
                            <code className="bg-white px-2 py-1 rounded text-xs border">
                              {(() => {
                                const address = nbcUser.wallet_address || nbcUser.public_key || '';
                                return `${address.substring(0, 8)}...${address.substring(address.length - 6)}`;
                              })()}
                            </code>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Balance Disponible */}
                {nbcUser?.success && userBalance && (
                  <div className="bg-linear-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-blue-900">{t('casino.available')}</div>
                        <div className="text-xs text-blue-600">Your NBC TALK PAY wallet</div>
                      </div>
                      {isLoadingBalance ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">{t('common.loading')}</span>
                        </div>
                      ) : (
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-900">{userBalance.usdt} USDT</div>
                          {displayCurrency !== "USD" && (
                            <div className="text-xs text-blue-600">≈ {getCurrencySymbol(displayCurrency)}{(parseFloat(userBalance.usdt) * exchangeRate).toFixed(2)} {displayCurrency}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="amount">{t('casino.amount')}</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        id="amount"
                        type="number"
                        placeholder={minAmount.toString()}
                        value={amount}
                        onChange={handleAmountChange}
                        className={`text-lg transition-all duration-200 focus:scale-[1.02] ${isAmountPrefilled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        disabled={isLoading || isAmountPrefilled}
                      />
                    </div>
                    <Select 
                      value={selectedCurrency} 
                      onValueChange={(value: string) => setSelectedCurrency(value)}
                      disabled={isAmountPrefilled}
                    >
                      <SelectTrigger className={`w-24 ${isAmountPrefilled ? 'bg-gray-100 cursor-not-allowed' : ''}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USDT</SelectItem>
                        <SelectItem value={displayCurrency}>{getCurrencySymbol(displayCurrency)} {displayCurrency}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Mostrar detalles de pago siempre cuando hay cantidad válida */}
                  {amount && Number.parseFloat(amount) > 0 && (
                    <div className="space-y-2">
                      {selectedCurrency !== "USD" && exchangeRate && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <TrendingUp className="h-3 w-3" />
                          <span>{t('casino.equivalent_amount')}: {usdtAmount.toFixed(2)} USDT</span>
                          <span className="text-xs opacity-70">(Rate: {exchangeRate.toFixed(4)})</span>
                        </div>
                      )}
                      {selectedCurrency === "USD" && exchangeRate && displayCurrency !== "USD" && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <TrendingUp className="h-3 w-3" />
                          <span>≈ {getCurrencySymbol(displayCurrency)}{(Number.parseFloat(amount) * exchangeRate).toFixed(2)} {displayCurrency}</span>
                          <span className="text-xs opacity-70">(Rate: {exchangeRate.toFixed(4)})</span>
                        </div>
                      )}
                      
                      {/* Desglose de comisiones - Mostrar siempre */}
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                        <div className="text-sm font-medium text-blue-800">{t('casino.payment_details')}</div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-blue-600">{t('casino.amount')}:</span>
                            <span className="font-mono">
                              {selectedCurrency !== "USD" 
                                ? `${usdtAmount.toFixed(2)} USDT`
                                : `${amount} USDT`
                              }
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-600">{t('casino.network_fee')}:</span>
                            <span className="font-mono">
                              {isLoadingFees ? (
                                <Loader2 className="h-3 w-3 animate-spin inline" />
                              ) : (
                                `${currentFee.toFixed(2)} USDT`
                              )}
                            </span>
                          </div>
                          <div className="border-t border-blue-300 pt-1 mt-1">
                            <div className="flex justify-between font-medium">
                              <span className="text-blue-800">{t('casino.total_to_send')}:</span>
                              <span className="font-mono">
                                {selectedCurrency !== "USD" 
                                  ? `${(usdtAmount + currentFee).toFixed(2)} USDT`
                                  : `${(Number.parseFloat(amount) + currentFee).toFixed(2)} USDT`
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Validation Error Display */}
                  {validationError && (
                    <Alert variant="destructive" className="mt-3">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{validationError}</AlertDescription>
                    </Alert>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-purple-500" />
                    <span className="text-sm font-medium text-muted-foreground">{t('casino.select_amount')}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {getQuickDeposits().map((preset) => {
                      const isSelected = selectedQuickAmount === preset.amount;
                      
                      return (
                        <Button
                          key={preset.amount}
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleQuickAmountClick(preset.amount)}
                          disabled={isLoading || isAmountPrefilled}
                          className={`transition-all duration-200 hover:scale-105 flex flex-col gap-1 h-auto py-3 ${
                            isSelected 
                              ? "bg-linear-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white" 
                              : ""
                          } ${isAmountPrefilled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-center gap-1">
                            <span className="text-lg">{preset.icon}</span>
                            <span className="font-bold">{preset.amount} {selectedCurrency}</span>
                          </div>
                          <span className="text-xs opacity-80">{preset.label}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <Button
                  className="w-full transition-all duration-200 hover:scale-[1.02]"
                  onClick={handleAmountSubmit}
                  disabled={!amount || Number.parseFloat(amount) <= 0 || isLoading || !!validationError || (selectedCurrency !== "USD" && (Number.parseFloat(amount) / exchangeRate) < minAmount) || (selectedCurrency === "USD" && Number.parseFloat(amount) < minAmount)}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('common.please_wait')}
                    </div>
                  ) : (
                    t('casino.continue')
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* PIN Step - Using CasinoPinConfirmation directly */}
        {currentStep === "pin" && (
          <div className="animate-in slide-in-from-right-4 duration-300">
            <CasinoPinConfirmation
              transactionType="casino_deposit"
              transactionDetails={{
                balance: userBalance?.usdt || '0.00',
                amount: `${amount} USDT`,
                fee: `${currentFee.toFixed(2)} USDT`,
                total: `${totalAmount.toFixed(2)} USDT`,
                recipient: t('casino.casino_wallet'),
                method: t('casino.casino_deposit'),
                error: '',
              }}
              email={verifiedEmail}
              onSuccess={handlePinSuccess}
              onCancel={() => setCurrentStep('amount')}
              onError={(error) => console.error('PIN Error:', error)}
              onMaxAttemptsReached={() => {
                console.log('🚫 [MobileDepositFlow] Max PIN attempts reached, cancelling order');
                setCurrentStep('amount');
              }}
            />
          </div>
        )}

        {/* Payment Step */}
        {currentStep === "payment" && (
          <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {t('payment.title')}
                </CardTitle>
                <CardDescription>{t('casino.pin_signature_description', { amount: orderData.amountUsdt })}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Información del Usuario NBC Verificado */}
                {verifiedEmail && (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        ✓ {t('casino.verified_account')}: {verifiedEmail}
                      </span>
                    </div>
                    
                    {nbcUser?.success && nbcUser.user_data && (
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <User className="h-3 w-3" />
                        <span>{nbcUser.user_data.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          ID: {nbcUser.user_data.nbc_talk_id}
                        </Badge>
                      </div>
                    )}
                    
                    {nbcUser?.success && (nbcUser.wallet_address || nbcUser.public_key) && (
                      <div className="flex items-center gap-2 text-xs text-green-700 mt-1">
                        <Wallet className="h-3 w-3" />
                        <code className="bg-white px-2 py-1 rounded text-xs border">
                          {(() => {
                            const address = nbcUser.wallet_address || nbcUser.public_key || '';
                            return `${address.substring(0, 8)}...${address.substring(address.length - 6)}`;
                          })()}
                        </code>
                      </div>
                    )}
                  </div>
                )}

                {/* Balance Disponible */}
                {nbcUser?.success && userBalance && (
                  <div className="bg-linear-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-blue-900">Available Balance</div>
                        <div className="text-xs text-blue-600">Your NBC TALK PAY wallet</div>
                      </div>
                      {isLoadingBalance ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Loading...</span>
                        </div>
                      ) : (
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-900">{userBalance.usdt} USDT</div>
                          {displayCurrency !== "USD" && (
                            <div className="text-xs text-blue-600">≈ {getCurrencySymbol(displayCurrency)}{(parseFloat(userBalance.usdt) * exchangeRate).toFixed(2)} {displayCurrency}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="p-4 bg-muted rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Order ID:</span>
                    <span className="text-sm font-mono">{orderData.orderId}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('casino.amount')}:</span>
                    <span className="text-sm font-mono">{orderData.amountUsdt} USDT</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('casino.network_fee')}:</span>
                    <span className="text-sm font-mono">{currentFee.toFixed(2)} USDT</span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-2 font-medium">
                    <span className="text-sm">{t('casino.total_to_send')}:</span>
                    <span className="text-sm font-mono">{(parseFloat(orderData.amountUsdt) + currentFee).toFixed(2)} USDT</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{displayCurrency} {t('casino.value')}:</span>
                    {displayCurrency !== "USD" ? (
                      <span className="text-sm">{getCurrencySymbol(displayCurrency)}{((parseFloat(orderData.amountUsdt) + currentFee) * exchangeRate).toFixed(2)}</span>
                    ) : (
                      <span className="text-sm">${(parseFloat(orderData.amountUsdt) + currentFee).toFixed(2)}</span>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Payment Address</Label>
                  <div className="flex items-center gap-2">
                    <Input value={orderData.paymentAddress} readOnly className="font-mono text-xs" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(orderData.paymentAddress)}
                      className="transition-all duration-200 hover:scale-105"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div
                  className={`flex items-center justify-center gap-2 text-sm transition-colors duration-300 ${
                    timeLeft < 300 ? "text-red-600" : timeLeft < 900 ? "text-yellow-600" : "text-muted-foreground"
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  Expires in {formatTime(timeLeft)}
                </div>

                <Button
                  className="w-full transition-all duration-200 hover:scale-[1.02]"
                  onClick={handlePaymentSent}
                >
                  {t('payment.sign_payment')}
                </Button>

                <Button
                  variant="outline"
                  className="w-full bg-transparent transition-all duration-200 hover:scale-[1.02]"
                  onClick={() => setShowDownloadModal(true)}
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  {t('payment.download_app')}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center space-y-2">
                  <Shield className="h-8 w-8 text-primary mx-auto" />
                  <h3 className="font-medium">{t('payment.secure_payment')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('payment.security_message')}
                  </p>
                  <p className="text-sm text-orange-600 font-medium">
                    {t('payment.warning_message')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Status Step */}
        {currentStep === "status" && (
          <div className="animate-in slide-in-from-right-4 duration-300">
            <Card>
              <CardHeader className="text-center">
                {transactionError ? (
                  <>
                    <div className="mx-auto my-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                      <AlertTriangle className="h-10 w-10 text-red-600" />
                    </div>
                    <CardTitle className="text-2xl text-red-600">{t('error.title')}</CardTitle>
                    <CardDescription>
                      {transactionError}
                    </CardDescription>
                  </>
                ) : transactionId ? (
                  <>
                    <div className="mx-auto my-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <CardTitle className="text-2xl text-green-600">{t('success.title')}</CardTitle>
                    <CardDescription>
                      {t('success.description')}
                    </CardDescription>
                    {redirectCountdown !== null && redirectCountdown > 0 && (
                      <CardDescription className="text-sm text-muted-foreground mt-2">
                        {t('deposit.redirecting_in').replace('{seconds}', redirectCountdown.toString())}
                      </CardDescription>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mx-auto my-4 flex h-20 w-20 items-center justify-center">
                      <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                    </div>
                    <CardTitle className="text-2xl">{t('common.loading')}</CardTitle>
                    <CardDescription>
                      {t('payment.processing')}
                    </CardDescription>
                  </>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                {transactionId && (
                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <div className="text-sm font-medium">{t('transactions.transaction_details')}</div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('transactions.transaction_id')}:</span>
                      <span className="font-mono">{transactionId}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('transactions.amount')}:</span>
                      <span>{orderData.amountUsdt} USDT</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('transactions.network')}:</span>
                      <span>Polygon</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {transactionError ? (
                    <Button 
                      className="w-full"
                      onClick={() => setCurrentStep("amount")}
                    >
                      {t('error.try_again')}
                    </Button>
                  ) : transactionId ? (
                    <>
                      {/* Botón "Make Another Deposit" oculto - solo mostrar Back to Casino */}
                      {/* <Button 
                        className="w-full"
                        onClick={() => {
                          if (prefilledOrderData?.callback_url) {
                            window.location.href = prefilledOrderData.callback_url;
                          } else {
                            setCurrentStep("amount");
                            setAmount("");
                            setTransactionId(null);
                            setTransactionError(null);
                            onComplete?.(parseFloat(orderData.amountUsdt));
                          }
                        }}
                      >
                        {t('casino.make_another_deposit')}
                      </Button> */}
                      
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => {
                          if (prefilledOrderData?.callback_url) {
                            window.location.href = prefilledOrderData.callback_url;
                          } else if (onBack) {
                            onBack();
                          }
                        }}
                      >
                        {t('casino.back_to_casino')}
                      </Button>
                    </>
                  ) : null}

                  {!transactionId && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={onBack}
                    >
                      {t('casino.back')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border bg-muted/30">
        <div className="text-center">
          <p className="text-xs text-muted-foreground/70">
            © 2024 {t('casino.powered_by')} <span className="font-semibold text-muted-foreground">NBC BANQ</span>
          </p>
        </div>
      </div>

      {/* Modal de descarga de NBC TALK PAY */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center flex items-center justify-center gap-2">
              <Smartphone className="h-6 w-6 text-purple-600" />
              {t('email_verification.download_modal_title')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('email_verification.download_modal_description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-4 py-4">
            {/* App Store Button */}
            <Button
              variant="outline"
              className="w-full h-16 flex items-center justify-start gap-4 p-4 hover:bg-gray-50 transition-colors"
              onClick={() => window.open("https://apps.apple.com/am/app/nbc-talk/id1553055704", "_blank")}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-black rounded-lg text-white">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">{t('email_verification.download_app_store')}</div>
                <div className="text-sm text-gray-500">Descargar para iOS</div>
              </div>
              <ExternalLink className="h-4 w-4 ml-auto text-gray-400" />
            </Button>

            {/* Play Store Button */}
            <Button
              variant="outline"
              className="w-full h-16 flex items-center justify-start gap-4 p-4 hover:bg-gray-50 transition-colors"
              onClick={() => window.open("https://play.google.com/store/apps/details?id=com.beint.nbctalk.com&hl=es_419", "_blank")}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-linear-to-br from-green-400 via-blue-500 to-purple-600 rounded-lg text-white">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">{t('email_verification.download_play_store')}</div>
                <div className="text-sm text-gray-500">Descargar para Android</div>
              </div>
              <ExternalLink className="h-4 w-4 ml-auto text-gray-400" />
            </Button>
          </div>

          <div className="text-center pt-2">
            <p className="text-xs text-gray-500">
              Necesitarás la app para completar transacciones con NBC TALK PAY
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de verificación 2FA */}
      <TwoFactorModal
        isOpen={show2FAModal}
        onClose={() => setShow2FAModal(false)}
        onVerify={handle2FAVerify}
        method={twoFactorMethod}
        email={verifiedEmail}
        amount={parseFloat(orderData.amountUsdt)}
      />
    </div>
  )
}
